import { ArrowRight, ArrowDown } from "lucide-react";
import { useQuote } from "@/lib/quoteContext";
import LiveStatus from "@/components/site/LiveStatus";

const HERO_IMG =
  "https://images.unsplash.com/photo-1766678340672-51e6be3a64d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMGNvbnRhaW5lciUyMGFic3RyYWN0fGVufDB8fHx8MTc3ODAzOTEyOHww&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const { openQuote } = useQuote();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[78vh] md:min-h-[92vh] w-full overflow-hidden bg-ink"
    >
      <img
        src={HERO_IMG}
        alt="Cargo ship at night with distant port lights"
        className="absolute inset-0 w-full h-full object-cover opacity-75"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/55 to-ink/90" />
      <div className="absolute inset-0 grain" />

      {/* Giant faded logo watermark */}
      <img
        src="/logo.png"
        alt=""
        aria-hidden
        className="pointer-events-none select-none absolute -right-20 -bottom-32 md:right-[-8%] md:bottom-[-12%] w-[480px] md:w-[760px] lg:w-[860px] opacity-[0.07] mix-blend-screen"
      />

      {/* top meta bar */}
      <div className="absolute top-20 inset-x-0 hidden md:block">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between text-white/60 font-mono text-[11px] uppercase tracking-[0.25em] pt-6">
          <span>EST. YIWU / CHINA</span>
          <LiveStatus variant="dark" />
          <span>ISSUE N°15 · 2025</span>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-52 pb-20 md:pb-36">
        <p
          className="reveal font-mono text-[11px] md:text-xs uppercase tracking-[0.3em] text-signal mb-8"
          style={{ animationDelay: "50ms" }}
        >
          Your global sourcing partner · Yiwu / China
        </p>

        <h1
          className="reveal font-heading font-black text-white leading-[0.92] tracking-[-0.035em] text-[12vw] sm:text-[10vw] md:text-[8.5vw] lg:text-[118px] uppercase"
          style={{ animationDelay: "120ms" }}
        >
          Command your
          <br />
          <span className="inline-flex flex-wrap items-baseline gap-x-3 sm:gap-x-4 md:gap-x-6">
            <span>global</span>
            <span className="text-stroke-light">supply</span>
          </span>
          <br />
          <span className="text-signal">chain.</span>
        </h1>

        <div
          className="reveal mt-10 md:mt-12 max-w-2xl text-white/85 text-base md:text-lg leading-relaxed"
          style={{ animationDelay: "240ms" }}
        >
          End-to-end exporting, Yiwu sourcing, customs clearance and door-to-door logistics.
          Fifteen years of quiet precision for businesses that ship worldwide.
        </div>

        <div
          className="reveal mt-10 md:mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          style={{ animationDelay: "340ms" }}
        >
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); openQuote(); }}
            data-testid="hero-quote-cta"
            className="group inline-flex items-center gap-3 bg-signal hover:bg-signal-600 text-white px-7 py-4 font-mono text-[12px] uppercase tracking-[0.22em] transition-colors"
          >
            Get a Quote
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.8} />
          </a>
          <a
            href="#services"
            data-testid="hero-services-cta"
            className="group inline-flex items-center gap-3 border border-white/40 text-white px-7 py-4 font-mono text-[12px] uppercase tracking-[0.22em] hover:bg-white hover:text-ink-900 transition-colors"
          >
            Explore Services
          </a>
        </div>

        {/* Stats strip */}
        <div
          className="reveal mt-14 md:mt-28 grid grid-cols-4 gap-4 md:gap-12 border-t border-white/15 pt-6 md:pt-10"
          style={{ animationDelay: "440ms" }}
        >
          {[
            { n: "15+", l: "Years" },
            { n: "15+", l: "Countries" },
            { n: "8K+", l: "Shipments" },
            { n: "24/7", l: "Tracking" },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-heading font-black text-white text-2xl sm:text-3xl md:text-5xl leading-none tracking-tight">
                {s.n}
              </div>
              <div className="mt-2 md:mt-3 font-mono text-[9px] md:text-[11px] uppercase tracking-[0.18em] md:tracking-[0.22em] text-white/55">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>

      <a
        href="#services"
        className="absolute bottom-6 right-6 md:right-12 hidden md:flex items-center gap-2 text-white/60 hover:text-white font-mono text-[11px] uppercase tracking-[0.25em]"
        aria-label="Scroll down"
      >
        <span>Scroll</span>
        <ArrowDown className="w-3.5 h-3.5 animate-bounce" strokeWidth={1.8} />
      </a>
    </section>
  );
}
